import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useOutletContext } from 'react-router-dom';
import { API, auth, inputCls, Field, Modal, PageHead, PrimaryBtn, errMsg, fmtDate } from './finance/shared';
import { EmptyState } from '../../components/dashboard/TodaySchedule';
import Icon from '../../components/dashboard/DashboardIcons';
import { WhatsAppSettingsForm } from '../../components/organization/WhatsAppSettingsForm';
import ActionsMenu from './players/ActionsMenu';

const ROLE_LABELS = { admin: 'مدير المنشأة', manager: 'إداري', treasurer: 'أمين صندوق', coach: 'مدرب', analyst: 'محلل', secretary: 'سكرتير', moderator: 'مشرف', viewer: 'مشاهد' };
// الأدوار القابلة للإسناد من داخل المنشأة فقط — «مدير المنشأة» (admin) و«سوبر أدمن» لا يظهران هنا مطلقاً
const ASSIGNABLE_ROLE_LABELS = { manager: 'إداري', treasurer: 'أمين صندوق', coach: 'مدرب', analyst: 'محلل', secretary: 'سكرتير', moderator: 'مشرف', viewer: 'مشاهد' };

export default function SettingsPage() {
  const { user } = useOutletContext() || {};
  const canManageUsers = Boolean(user?.is_super_admin || user?.is_users_admin);
  const [tab, setTab] = useState('identity');
  const [toast, setToast] = useState(null);
  const notify = (m) => { setToast(m); setTimeout(() => setToast(null), 3000); };
  const tabs = [['identity', 'بيانات المنشأة والهوية'], ['parent_portal', 'بوابة ولي الأمر'], ['communication', 'إعدادات التواصل'], ['vat', 'الإعدادات المالية'], ['packages', 'باقات الاشتراك'], canManageUsers && ['users', 'المستخدمون والصلاحيات']].filter(Boolean);

  return (
    <div className="mx-auto max-w-5xl space-y-4" data-testid="settings-page">
      {toast && <div className="fixed top-16 left-1/2 z-50 -translate-x-1/2 rounded-lg bg-[#16233b] px-4 py-2 text-sm text-white shadow-lg">{toast}</div>}
      <PageHead title="الإعدادات" desc="بيانات المنشأة وهويتها البصرية وإدارة المستخدمين" />
      <div className="flex gap-1 rounded-xl border border-slate-200 bg-white p-1 overflow-x-auto" data-testid="settings-tabs">
        {tabs.map(([k, v]) => (
          <button key={k} onClick={() => setTab(k)} className={`shrink-0 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${tab === k ? 'bg-[#16233b] text-white' : 'text-slate-600 hover:bg-slate-50'}`} data-testid={`settings-tab-${k}`}>{v}</button>
        ))}
      </div>
      {tab === 'identity' && <IdentityTab notify={notify} />}
      {tab === 'parent_portal' && <ParentPortalTab notify={notify} />}
      {tab === 'communication' && (
        <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="settings-communication-tab">
          <WhatsAppSettingsForm />
        </div>
      )}
      {tab === 'vat' && <VatTab notify={notify} />}
      {tab === 'packages' && <PackagesTab notify={notify} />}
      {tab === 'users' && <UsersTab notify={notify} currentUser={user} />}
    </div>
  );
}

function useClubSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    try { const res = await axios.get(`${API}/club-settings`, auth()); setSettings(res.data); } catch { setSettings(null); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);
  return { settings, loading, reload: load };
}

function IdentityTab({ notify }) {
  const { settings, loading, reload } = useClubSettings();
  const [form, setForm] = useState({});
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (settings) setForm({
      club_name: settings.club_name || '',
      org_type: settings.org_type || 'both',
      app_name: settings.app_name || '',
      description: settings.club_description || settings.description || '',
      location: settings.club_city || settings.location || '',
      established_year: settings.club_established_year || settings.established_year || '',
      phone: settings.phone || settings.club_phone || '',
      email: settings.email || settings.club_email || '',
      primary_color: settings.primary_color || '#16233b',
      secondary_color: settings.secondary_color || '#0d9488',
      logo_file_id: settings.logo_file_id || null,
      website_url: settings.website_url || '',
      instagram_url: settings.instagram_url || '',
      twitter_url: settings.twitter_url || '',
      facebook_url: settings.facebook_url || '',
    });
  }, [settings]);

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true); setErr(null);
    const fd = new FormData(); fd.append('file', file);
    try {
      const res = await axios.post(`${API}/files/upload`, fd, auth());
      const fileId = res.data?.file_id || res.data?.id;
      await axios.put(`${API}/club-settings`, { club_name: settings.club_name, logo_file_id: fileId }, auth());
      notify('تم تحديث الشعار'); reload();
    } catch (e2) { setErr(errMsg(e2, 'فشل رفع الشعار')); }
    setUploading(false);
  };

  const save = async (e) => {
    e.preventDefault(); setErr(null);
    if (!form.club_name?.trim()) { setErr('اسم المنشأة مطلوب'); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/club-settings`, {
        club_name: form.club_name.trim(),
        org_type: form.org_type || 'both',
        app_name: form.app_name || null,
        description: form.description || null,
        location: form.location || null,
        established_year: form.established_year ? Number(form.established_year) : null,
        phone: form.phone || null,
        email: form.email || null,
        primary_color: form.primary_color,
        secondary_color: form.secondary_color,
        website_url: form.website_url || null,
        instagram_url: form.instagram_url || null,
        twitter_url: form.twitter_url || null,
        facebook_url: form.facebook_url || null,
      }, auth());
      notify('تم حفظ بيانات المنشأة والهوية');
    } catch (e2) { setErr(errMsg(e2, 'فشل الحفظ — تأكد من صلاحياتك')); }
    setSaving(false);
  };

  if (loading) return <div className="h-64 animate-pulse rounded-xl bg-slate-100" />;
  const logoUrl = form.logo_file_id ? `${API}/files/${form.logo_file_id}` : null;

  return (
    <div className="space-y-4">
      <div className="mb-1 flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3">
        <div>
          <div className="text-sm font-bold text-slate-800">معالج الإعداد الأولي</div>
          <div className="text-xs text-slate-500">أعد فتحه لمراجعة بيانات المنشأة والفئات والضريبة خطوة بخطوة</div>
        </div>
        <button type="button" onClick={() => window.dispatchEvent(new CustomEvent('open-onboarding-wizard'))} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:border-teal-300" data-testid="settings-reopen-onboarding-btn">فتح معالج الإعداد</button>
      </div>

      <form onSubmit={save} className="space-y-4" data-testid="settings-identity-general-form">
        {err && <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="settings-general-error">{err}</div>}

        {/* بطاقة الهوية: شعار + اسم المنشأة + معاينة ألوان الهوية في مكان واحد */}
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm" data-testid="settings-identity-hero">
          <div className="h-20 w-full" style={{ background: `linear-gradient(120deg, ${form.primary_color || '#16233b'}, ${form.secondary_color || '#0d9488'})` }} />
          <div className="-mt-10 flex flex-col items-center gap-4 px-5 pb-5 sm:flex-row sm:items-end">
            <div className="relative shrink-0">
              <div className="flex h-24 w-24 items-center justify-center overflow-hidden rounded-2xl border-4 border-white bg-white shadow-lg">
                {logoUrl ? <img src={logoUrl} alt="الشعار" className="h-full w-full object-contain" /> : <Icon name="building" className="h-9 w-9 text-slate-300" />}
              </div>
              <label className="absolute -bottom-1.5 -left-1.5 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border-2 border-white bg-teal-600 text-white shadow-md transition-colors hover:bg-teal-700" title="تغيير الشعار" data-testid="settings-logo-change-btn">
                <Icon name="upload" className="h-3.5 w-3.5" />
                <input type="file" accept="image/*" className="hidden" onChange={uploadLogo} data-testid="settings-logo-input" />
              </label>
              {uploading && <div className="absolute inset-0 flex items-center justify-center rounded-2xl bg-white/70 text-[10px] font-bold text-teal-700">جارٍ الرفع...</div>}
            </div>
            <div className="flex-1 pt-1 text-center sm:pt-6 sm:text-right">
              <p className="text-lg font-black text-slate-900" data-testid="settings-identity-preview-name">{form.club_name || 'اسم المنشأة'}</p>
              <p className="text-xs text-slate-500">{form.location || 'المدينة'}{form.established_year ? ` · تأسست ${form.established_year}` : ''}</p>
            </div>
            <div className="flex items-center gap-3 pb-1">
              <ColorSwatch label="أساسي" value={form.primary_color || '#16233b'} onChange={(v) => setForm({ ...form, primary_color: v })} testId="settings-primary-color" />
              <ColorSwatch label="ثانوي" value={form.secondary_color || '#0d9488'} onChange={(v) => setForm({ ...form, secondary_color: v })} testId="settings-secondary-color" />
            </div>
          </div>
        </div>

        {/* البيانات الأساسية */}
        <div className="grid grid-cols-1 gap-3 rounded-xl border border-slate-200 bg-white p-5 sm:grid-cols-2">
          <Field label="اسم المنشأة" required><input className={inputCls} value={form.club_name || ''} onChange={(e) => setForm({ ...form, club_name: e.target.value })} data-testid="settings-input-name" /></Field>
          <Field label="نوع المنشأة" required>
            <select className={inputCls} value={form.org_type || 'both'} onChange={(e) => setForm({ ...form, org_type: e.target.value })} data-testid="settings-input-org-type">
              <option value="club">نادي رياضي</option>
              <option value="academy">أكاديمية رياضية</option>
              <option value="both">نادي وأكاديمية (كلاهما)</option>
            </select>
          </Field>
          <Field label="اسم المنصة (يظهر كعنوان الصفحة)"><input className={inputCls} value={form.app_name || ''} onChange={(e) => setForm({ ...form, app_name: e.target.value })} /></Field>
          <Field label="المدينة"><input className={inputCls} value={form.location || ''} onChange={(e) => setForm({ ...form, location: e.target.value })} /></Field>
          <Field label="سنة التأسيس"><input type="number" className={inputCls} value={form.established_year || ''} onChange={(e) => setForm({ ...form, established_year: e.target.value })} /></Field>
          <Field label="الهاتف"><input dir="ltr" className={inputCls} value={form.phone || ''} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
          <div className="col-span-full"><Field label="البريد الإلكتروني"><input dir="ltr" type="email" className={inputCls} value={form.email || ''} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field></div>
          <div className="col-span-full"><Field label="نبذة عن المنشأة"><textarea rows={3} className={inputCls} value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field></div>
        </div>

        {/* روابط الموقع والتواصل الاجتماعي — تُستخدم في تذييل استوديو التقارير */}
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <p className="mb-3 text-sm font-bold text-slate-800">روابط الموقع والتواصل الاجتماعي</p>
          <p className="mb-3 -mt-2 text-xs text-slate-400">تظهر هذه الروابط اختيارياً في تذييل تقارير استوديو التقارير (PDF) — اتركها فارغة لإخفائها</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="الموقع الإلكتروني"><input dir="ltr" placeholder="https://..." className={inputCls} value={form.website_url || ''} onChange={(e) => setForm({ ...form, website_url: e.target.value })} data-testid="settings-input-website" /></Field>
            <Field label="انستقرام"><input dir="ltr" placeholder="https://instagram.com/..." className={inputCls} value={form.instagram_url || ''} onChange={(e) => setForm({ ...form, instagram_url: e.target.value })} data-testid="settings-input-instagram" /></Field>
            <Field label="تويتر / X"><input dir="ltr" placeholder="https://x.com/..." className={inputCls} value={form.twitter_url || ''} onChange={(e) => setForm({ ...form, twitter_url: e.target.value })} data-testid="settings-input-twitter" /></Field>
            <Field label="فيسبوك"><input dir="ltr" placeholder="https://facebook.com/..." className={inputCls} value={form.facebook_url || ''} onChange={(e) => setForm({ ...form, facebook_url: e.target.value })} data-testid="settings-input-facebook" /></Field>
          </div>
        </div>

        <div className="flex justify-end"><PrimaryBtn type="submit" disabled={saving} data-testid="settings-general-save">{saving ? 'جارٍ الحفظ...' : 'حفظ البيانات والهوية'}</PrimaryBtn></div>
      </form>
    </div>
  );
}

function ColorSwatch({ label, value, onChange, testId }) {
  return (
    <label className="flex cursor-pointer flex-col items-center gap-1">
      <span className="relative h-9 w-9 overflow-hidden rounded-full border-2 border-white shadow" style={{ background: value }}>
        <input type="color" value={value} onChange={(e) => onChange(e.target.value)} className="absolute inset-0 h-full w-full cursor-pointer opacity-0" data-testid={testId} />
      </span>
      <span className="text-[10px] font-semibold text-white/90 drop-shadow sm:text-slate-500 sm:drop-shadow-none">{label}</span>
    </label>
  );
}

function ParentPortalTab({ notify }) {
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await axios.get(`${API}/clubs/current`, auth()); setVisible(Boolean(res.data?.parent_directory_visible)); } catch { /* noop */ }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async () => {
    const next = !visible;
    setSaving(true);
    try {
      await axios.put(`${API}/parent-directory-visibility`, { visible: next }, auth());
      setVisible(next);
      notify(next ? 'أصبحت المنشأة ظاهرة الآن لأولياء الأمور في الاستكشاف' : 'تم إخفاء المنشأة من استكشاف أولياء الأمور');
    } catch (e2) { notify(errMsg(e2, 'فشل تحديث الإعداد')); }
    setSaving(false);
  };

  if (loading) return <div className="h-32 animate-pulse rounded-xl bg-slate-100" />;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="settings-parent-portal">
      <label className="flex items-center justify-between gap-3">
        <div>
          <div className="text-sm font-bold text-slate-800">الظهور في بوابة ولي الأمر (الاستكشاف)</div>
          <p className="mt-1 text-xs text-slate-500">عند التفعيل، تظهر منشأتك وبرامجها لكل أولياء الأمور في صفحة "استكشاف الأكاديميات"، ويمكنهم إرسال طلبات تسجيل مباشرة. معطّلة افتراضياً.</p>
        </div>
        <input type="checkbox" className="h-5 w-9 shrink-0" checked={visible} disabled={saving} onChange={toggle} data-testid="settings-parent-portal-toggle" />
      </label>
      <div className={`mt-4 rounded-lg px-3 py-2 text-xs font-semibold ${visible ? 'bg-teal-50 text-teal-700' : 'bg-slate-50 text-slate-500'}`} data-testid="settings-parent-portal-status">
        {visible ? 'منشأتك ظاهرة حالياً لأولياء الأمور في الاستكشاف' : 'منشأتك غير ظاهرة حالياً في استكشاف أولياء الأمور'}
      </div>
    </div>
  );
}

function VatTab({ notify }) {
  const { settings, loading, reload } = useClubSettings();
  const [form, setForm] = useState({});
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settings) setForm({
      vat_registered: settings.vat_registered || false,
      vat_number: settings.vat_number || '',
      vat_legal_name: settings.vat_legal_name || '',
      vat_national_address: settings.vat_national_address || '',
      vat_rate: settings.vat_rate ?? 15,
      prices_include_vat: settings.prices_include_vat || false,
      vat_effective_date: settings.vat_effective_date || '',
      enforce_finance_separation: settings.enforce_finance_separation || false,
    });
  }, [settings]);

  const save = async (e) => {
    e.preventDefault(); setErr(null);
    if (form.vat_registered && !form.vat_number?.trim()) { setErr('الرقم الضريبي مطلوب عند تسجيل المنشأة في ضريبة القيمة المضافة'); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/club-settings`, {
        club_name: settings.club_name,
        vat_registered: form.vat_registered,
        vat_number: form.vat_registered ? form.vat_number.trim() : null,
        vat_legal_name: form.vat_legal_name || null,
        vat_national_address: form.vat_national_address || null,
        vat_rate: Number(form.vat_rate) || 15,
        prices_include_vat: form.prices_include_vat,
        vat_effective_date: form.vat_effective_date || null,
        enforce_finance_separation: form.enforce_finance_separation,
      }, auth());
      notify('تم حفظ إعدادات الضريبة'); reload();
    } catch (e2) { setErr(errMsg(e2, 'فشل الحفظ')); }
    setSaving(false);
  };

  if (loading) return <div className="h-48 animate-pulse rounded-xl bg-slate-100" />;
  return (
    <div className="space-y-6">
      <form onSubmit={save} className="space-y-4" data-testid="settings-vat-form">
        {err && <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="settings-vat-error">{err}</div>}
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <label className="flex items-center justify-between gap-3">
            <div>
              <div className="text-sm font-bold text-slate-800">المنشأة مسجلة في ضريبة القيمة المضافة</div>
              <div className="text-xs text-slate-500">معطّلة افتراضياً — عند التفعيل تُضاف الضريبة تلقائياً على الفواتير الجديدة فقط</div>
            </div>
            <input type="checkbox" className="h-5 w-9" checked={form.vat_registered || false} onChange={(e) => setForm({ ...form, vat_registered: e.target.checked })} data-testid="settings-vat-registered-toggle" />
          </label>
        </div>
        {form.vat_registered && (
          <div className="grid grid-cols-1 gap-3 rounded-xl border border-slate-200 bg-white p-5 sm:grid-cols-2">
            <Field label="الرقم الضريبي" required><input dir="ltr" className={inputCls} value={form.vat_number || ''} onChange={(e) => setForm({ ...form, vat_number: e.target.value })} data-testid="settings-vat-number" /></Field>
            <Field label="نسبة الضريبة %"><input dir="ltr" type="number" min="0" max="100" step="0.5" className={inputCls} value={form.vat_rate ?? 15} onChange={(e) => setForm({ ...form, vat_rate: e.target.value })} data-testid="settings-vat-rate" /></Field>
            <Field label="الاسم القانوني"><input className={inputCls} value={form.vat_legal_name || ''} onChange={(e) => setForm({ ...form, vat_legal_name: e.target.value })} data-testid="settings-vat-legal-name" /></Field>
            <Field label="تاريخ بدء تطبيق الضريبة"><input type="date" className={inputCls} value={form.vat_effective_date || ''} onChange={(e) => setForm({ ...form, vat_effective_date: e.target.value })} /></Field>
            <div className="col-span-full"><Field label="العنوان الوطني للمنشأة"><textarea rows={2} className={inputCls} value={form.vat_national_address || ''} onChange={(e) => setForm({ ...form, vat_national_address: e.target.value })} /></Field></div>
            <label className="col-span-full flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={form.prices_include_vat || false} onChange={(e) => setForm({ ...form, prices_include_vat: e.target.checked })} /> الأسعار المدخلة تشمل الضريبة (وليست قبلها)
            </label>
          </div>
        )}
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <label className="flex items-center justify-between gap-3">
            <div>
              <div className="text-sm font-bold text-slate-800">فصل الصلاحيات المالية</div>
              <div className="text-xs text-slate-500">عند التفعيل، لا يمكن لموظف اعتماد مصروف أنشأه هو نفسه</div>
            </div>
            <input type="checkbox" className="h-5 w-9" checked={form.enforce_finance_separation || false} onChange={(e) => setForm({ ...form, enforce_finance_separation: e.target.checked })} data-testid="settings-finance-separation-toggle" />
          </label>
        </div>
        <div className="flex justify-end"><PrimaryBtn type="submit" disabled={saving} data-testid="settings-vat-save">{saving ? 'جارٍ الحفظ...' : 'حفظ إعدادات الضريبة'}</PrimaryBtn></div>
      </form>
      <BankAccountsSection notify={notify} />
    </div>
  );
}

function BankAccountsSection({ notify }) {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await axios.get(`${API}/bank-accounts`, auth()); setAccounts(Array.isArray(res.data) ? res.data : []); } catch { setAccounts([]); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openForm = (a) => {
    setEditing(a || null);
    setForm(a ? { bank_name: a.bank_name, beneficiary_name: a.beneficiary_name, iban: a.iban || '', account_number: a.account_number || '', is_default: a.is_default || false, status: a.status || 'active' }
      : { bank_name: '', beneficiary_name: '', iban: '', account_number: '', is_default: accounts.length === 0, status: 'active' });
    setErr(null); setModal(true);
  };

  const submit = async (e) => {
    e.preventDefault(); setErr(null);
    if (!form.bank_name?.trim() || !form.beneficiary_name?.trim() || !form.iban?.trim()) { setErr('اسم البنك والمستفيد ورقم الآيبان مطلوبة'); return; }
    setSaving(true);
    const payload = { bank_name: form.bank_name.trim(), beneficiary_name: form.beneficiary_name.trim(), iban: form.iban.trim(), account_number: form.account_number?.trim() || null, is_default: form.is_default, status: form.status };
    try {
      if (editing) await axios.put(`${API}/bank-accounts/${editing.id}`, payload, auth());
      else await axios.post(`${API}/bank-accounts`, payload, auth());
      setModal(false); notify(editing ? 'تم تحديث الحساب البنكي' : 'تمت إضافة الحساب البنكي'); load();
    } catch (e2) { setErr(errMsg(e2, 'فشل الحفظ')); }
    setSaving(false);
  };

  const toggleStatus = async (a) => {
    try {
      await axios.put(`${API}/bank-accounts/${a.id}`, { status: a.status === 'active' ? 'inactive' : 'active' }, auth());
      notify(a.status === 'active' ? 'تم تعطيل الحساب' : 'تم تفعيل الحساب'); load();
    } catch (e2) { notify(errMsg(e2, 'تعذر تحديث الحالة')); }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white" data-testid="settings-bank-accounts">
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
        <div>
          <span className="text-sm font-bold text-slate-800">الحسابات البنكية ({accounts.length})</span>
          <p className="text-xs text-slate-500">تُستخدم لإظهار بيانات التحويل البنكي داخل عروض الأسعار فقط</p>
        </div>
        <PrimaryBtn onClick={() => openForm(null)} data-testid="bank-account-add-btn"><Icon name="plus" className="w-4 h-4" /> إضافة حساب</PrimaryBtn>
      </div>
      {loading ? <div className="space-y-2 p-4">{[1, 2].map((i) => <div key={i} className="h-11 animate-pulse rounded-lg bg-slate-100" />)}</div>
        : accounts.length === 0 ? <div className="p-6"><EmptyState icon="banknotes" text="لا توجد حسابات بنكية بعد" testId="bank-accounts-empty" /></div>
        : (
          <table className="w-full text-sm">
            <thead><tr className="border-b border-slate-100 text-right text-xs text-slate-500">
              <th className="px-4 py-2 font-semibold">البنك</th><th className="px-2 py-2 font-semibold">المستفيد</th>
              <th className="px-2 py-2 font-semibold">الآيبان</th><th className="px-2 py-2 font-semibold">الحالة</th><th className="px-2 py-2"></th>
            </tr></thead>
            <tbody>
              {accounts.map((a) => (
                <tr key={a.id} className="border-b border-slate-50" data-testid="bank-account-row">
                  <td className="px-4 py-2 font-semibold text-slate-800">{a.bank_name}{a.is_default && <span className="ms-2 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-700">افتراضي</span>}</td>
                  <td className="px-2 py-2 text-slate-600">{a.beneficiary_name}</td>
                  <td className="px-2 py-2 text-slate-500" dir="ltr">{a.iban_formatted || a.iban}</td>
                  <td className="px-2 py-2">
                    <button onClick={() => toggleStatus(a)} className={`rounded-full px-2 py-0.5 text-xs font-semibold ${a.status === 'active' ? 'bg-teal-50 text-teal-700' : 'bg-slate-100 text-slate-500'}`} data-testid="bank-account-status-toggle">
                      {a.status === 'active' ? 'فعال' : 'غير فعال'}
                    </button>
                  </td>
                  <td className="px-2 py-2 text-left">
                    <button onClick={() => openForm(a)} className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 hover:text-teal-700" data-testid="bank-account-edit-btn"><Icon name="cog" className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

      <Modal open={modal} title={editing ? 'تعديل حساب بنكي' : 'إضافة حساب بنكي'} onClose={() => setModal(false)}>
        {err && <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="bank-account-form-error">{err}</div>}
        <form onSubmit={submit} className="space-y-3">
          <Field label="اسم البنك" required><input className={inputCls} value={form.bank_name || ''} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} data-testid="bank-account-input-name" /></Field>
          <Field label="اسم المستفيد" required><input className={inputCls} value={form.beneficiary_name || ''} onChange={(e) => setForm({ ...form, beneficiary_name: e.target.value })} data-testid="bank-account-input-beneficiary" /></Field>
          <Field label="رقم الآيبان (IBAN)" required><input dir="ltr" className={inputCls} placeholder="SA0380000000608010167519" value={form.iban || ''} onChange={(e) => setForm({ ...form, iban: e.target.value })} data-testid="bank-account-input-iban" /></Field>
          <Field label="رقم الحساب (اختياري)"><input dir="ltr" className={inputCls} value={form.account_number || ''} onChange={(e) => setForm({ ...form, account_number: e.target.value })} data-testid="bank-account-input-number" /></Field>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={form.is_default || false} onChange={(e) => setForm({ ...form, is_default: e.target.checked })} data-testid="bank-account-input-default" /> حساب افتراضي
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={form.status !== 'inactive'} onChange={(e) => setForm({ ...form, status: e.target.checked ? 'active' : 'inactive' })} data-testid="bank-account-input-active" /> فعال
            </label>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setModal(false)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
            <PrimaryBtn type="submit" disabled={saving} data-testid="bank-account-form-submit">{saving ? 'جارٍ الحفظ...' : 'حفظ'}</PrimaryBtn>
          </div>
        </form>
      </Modal>
    </div>
  );
}

const DURATION_UNIT_LABELS = { day: 'يوم', week: 'أسبوع', month: 'شهر', year: 'سنة' };

function PackagesTab({ notify }) {
  const [packages, setPackages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [err, setErr] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pkgRes, catRes] = await Promise.all([
        axios.get(`${API}/subscription-packages?include_inactive=true`, auth()),
        axios.get(`${API}/categories`, auth()),
      ]);
      setPackages(Array.isArray(pkgRes.data) ? pkgRes.data : []);
      setCategories(Array.isArray(catRes.data) ? catRes.data : []);
    } catch { setPackages([]); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openForm = (p) => {
    setEditing(p || null);
    setForm(p ? { name: p.name, duration_value: p.duration_value, duration_unit: p.duration_unit, price: p.price, category_ids: p.category_ids || [], is_active: p.is_active, sessions_mode: p.sessions_mode || 'unlimited', sessions_per_week: p.sessions_per_week ?? '', total_sessions: p.total_sessions ?? '', allow_player_override: p.allow_player_override !== false }
      : { name: '', duration_value: 1, duration_unit: 'month', price: '', category_ids: [], is_active: true, sessions_mode: 'unlimited', sessions_per_week: '', total_sessions: '', allow_player_override: true });
    setErr(null); setModal(true);
  };

  const toggleCategory = (id) => {
    setForm((f) => ({ ...f, category_ids: f.category_ids.includes(id) ? f.category_ids.filter((c) => c !== id) : [...f.category_ids, id] }));
  };

  const submit = async (e) => {
    e.preventDefault(); setErr(null);
    if (!form.name?.trim()) { setErr('اسم الباقة مطلوب'); return; }
    if (!form.duration_value || form.duration_value <= 0) { setErr('مدة الباقة يجب أن تكون رقماً أكبر من صفر'); return; }
    if (form.price === '' || form.price < 0) { setErr('سعر الباقة مطلوب'); return; }
    const mode = form.sessions_mode || 'unlimited';
    if (mode === 'weekly' && !form.sessions_per_week) { setErr('حدد عدد الحصص في الأسبوع'); return; }
    if (mode === 'total' && !form.total_sessions) { setErr('حدد عدد الحصص الإجمالي للباقة'); return; }
    const payload = {
      name: form.name.trim(), duration_value: Number(form.duration_value), duration_unit: form.duration_unit,
      price: Number(form.price), category_ids: form.category_ids, is_active: form.is_active,
      sessions_mode: mode,
      sessions_per_week: mode === 'weekly' ? Number(form.sessions_per_week) : null,
      total_sessions: mode === 'total' ? Number(form.total_sessions) : null,
      allow_player_override: form.allow_player_override !== false,
    };
    try {
      if (editing) await axios.put(`${API}/subscription-packages/${editing.id}`, payload, auth());
      else await axios.post(`${API}/subscription-packages`, payload, auth());
      setModal(false); notify('تم حفظ الباقة'); load();
    } catch (e2) { setErr(errMsg(e2, 'فشل الحفظ — تأكد من صلاحياتك المالية')); }
  };

  const remove = async () => {
    try { await axios.delete(`${API}/subscription-packages/${confirmDel.id}`, auth()); notify('تم حذف الباقة'); setConfirmDel(null); load(); }
    catch (e2) { notify(errMsg(e2, 'تعذر الحذف')); setConfirmDel(null); }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white" data-testid="settings-packages">
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
        <span className="text-sm font-bold text-slate-800">كتالوج باقات الاشتراك ({packages.length})</span>
        <PrimaryBtn onClick={() => openForm(null)} data-testid="package-add-btn"><Icon name="plus" className="w-4 h-4" /> إضافة باقة</PrimaryBtn>
      </div>
      {loading ? <div className="space-y-2 p-4">{[1, 2, 3].map((i) => <div key={i} className="h-11 animate-pulse rounded-lg bg-slate-100" />)}</div>
        : packages.length === 0 ? <div className="p-6"><EmptyState icon="document" text="لا توجد باقات اشتراك بعد" testId="packages-empty" /></div>
        : (
          <table className="w-full text-sm">
            <thead><tr className="border-b border-slate-100 text-right text-xs text-slate-500">
              <th className="px-4 py-2 font-semibold">الباقة</th>
              <th className="px-2 py-2 font-semibold">المدة</th>
              <th className="px-2 py-2 font-semibold">السعر</th>
              <th className="px-2 py-2 font-semibold">الحصص</th>
              <th className="px-2 py-2 font-semibold">الفئات</th>
              <th className="px-2 py-2 font-semibold">الحالة</th>
              <th className="px-2 py-2"></th>
            </tr></thead>
            <tbody>
              {packages.map((p) => (
                <tr key={p.id} className="border-b border-slate-50" data-testid="package-row">
                  <td className="px-4 py-2 font-semibold text-slate-800">{p.name}</td>
                  <td className="px-2 py-2 text-slate-600">{p.duration_value} {DURATION_UNIT_LABELS[p.duration_unit]}</td>
                  <td className="px-2 py-2 text-slate-600" dir="rtl">{p.price} ر.س</td>
                  <td className="px-2 py-2 text-xs" data-testid="package-sessions-cell">
                    {p.sessions_mode === 'weekly' && p.sessions_per_week
                      ? <span className="rounded-full bg-sky-50 px-2 py-0.5 font-bold text-sky-700">{p.sessions_per_week} حصص/أسبوع</span>
                      : p.sessions_mode === 'total' && p.total_sessions
                      ? <span className="rounded-full bg-indigo-50 px-2 py-0.5 font-bold text-indigo-700">{p.total_sessions} حصة إجمالاً</span>
                      : <span className="text-slate-400">غير مقيّدة</span>}
                  </td>
                  <td className="px-2 py-2 text-slate-500 text-xs">{p.category_ids?.length ? `${p.category_ids.length} فئة محددة` : 'كل الفئات'}</td>
                  <td className="px-2 py-2"><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${p.is_active ? 'bg-teal-50 text-teal-700' : 'bg-slate-100 text-slate-500'}`}>{p.is_active ? 'مفعلة' : 'متوقفة'}</span></td>
                  <td className="px-2 py-2">
                    <div className="flex justify-end gap-1">
                      <button onClick={() => openForm(p)} className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 hover:text-teal-700" data-testid="package-edit-btn"><Icon name="cog" className="w-4 h-4" /></button>
                      <button onClick={() => setConfirmDel(p)} className="rounded-lg p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600" data-testid="package-del-btn"><Icon name="trash" className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

      <Modal open={modal} title={editing ? 'تعديل الباقة' : 'إضافة باقة'} onClose={() => setModal(false)}>
        {err && <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="package-form-error">{err}</div>}
        <form onSubmit={submit} className="space-y-3">
          <Field label="اسم الباقة" required><input className={inputCls} value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="package-input-name" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="المدة" required><input type="number" min="1" className={inputCls} value={form.duration_value || ''} onChange={(e) => setForm({ ...form, duration_value: e.target.value })} data-testid="package-input-duration-value" /></Field>
            <Field label="وحدة المدة">
              <select className={inputCls} value={form.duration_unit || 'month'} onChange={(e) => setForm({ ...form, duration_unit: e.target.value })} data-testid="package-input-duration-unit">
                {Object.entries(DURATION_UNIT_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </Field>
          </div>
          <Field label="السعر (ر.س)" required><input type="number" min="0" step="0.01" className={inputCls} value={form.price ?? ''} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="package-input-price" /></Field>
          <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3" data-testid="package-sessions-block">
            <div className="mb-2 text-xs font-extrabold text-slate-700">الحصص التدريبية</div>
            <p className="mb-2.5 text-[11px] leading-relaxed text-slate-500">
              حدّد نظام الحصص لهذه الباقة. ويمكنك السماح بتخصيص عدد مختلف لكل لاعب (مثال: لاعب 3 حصص/أسبوع وآخر مرتين) من نافذة اللاعب.
            </p>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <Field label="نظام الحصص">
                <select className={inputCls} value={form.sessions_mode || 'unlimited'} onChange={(e) => setForm({ ...form, sessions_mode: e.target.value })} data-testid="package-input-sessions-mode">
                  <option value="unlimited">غير مقيّدة (حصص مفتوحة)</option>
                  <option value="weekly">عدد حصص في الأسبوع</option>
                  <option value="total">رصيد حصص إجمالي للباقة</option>
                </select>
              </Field>
              {form.sessions_mode === 'weekly' && (
                <Field label="حصص في الأسبوع" required>
                  <input type="number" min="1" max="14" className={inputCls} value={form.sessions_per_week ?? ''} onChange={(e) => setForm({ ...form, sessions_per_week: e.target.value })} data-testid="package-input-sessions-per-week" />
                </Field>
              )}
              {form.sessions_mode === 'total' && (
                <Field label="إجمالي الحصص" required>
                  <input type="number" min="1" max="500" className={inputCls} value={form.total_sessions ?? ''} onChange={(e) => setForm({ ...form, total_sessions: e.target.value })} data-testid="package-input-total-sessions" />
                </Field>
              )}
            </div>
            {form.sessions_mode !== 'unlimited' && (
              <label className="mt-2 flex items-center gap-2 text-xs font-semibold text-slate-700">
                <input type="checkbox" className="h-4 w-4 accent-teal-600" checked={form.allow_player_override !== false} onChange={(e) => setForm({ ...form, allow_player_override: e.target.checked })} data-testid="package-input-allow-override" />
                السماح بتخصيص عدد الحصص لكل لاعب
              </label>
            )}
          </div>
          <Field label="الفئات (اختياري — بلا تحديد = تصلح لكل الفئات)">
            <div className="flex flex-wrap gap-2 rounded-lg border border-slate-200 p-2">
              {categories.map((c) => (
                <label key={c.id} className={`cursor-pointer rounded-full px-3 py-1 text-xs font-semibold ${form.category_ids?.includes(c.id) ? 'bg-[#16233b] text-white' : 'bg-slate-100 text-slate-600'}`}>
                  <input type="checkbox" className="hidden" checked={form.category_ids?.includes(c.id) || false} onChange={() => toggleCategory(c.id)} data-testid={`package-category-check-${c.id}`} />
                  {c.name}
                </label>
              ))}
              {categories.length === 0 && <span className="text-xs text-slate-400">لا توجد فئات</span>}
            </div>
          </Field>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={form.is_active !== false} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} data-testid="package-input-active" /> باقة مفعلة
          </label>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setModal(false)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
            <PrimaryBtn type="submit" data-testid="package-form-submit">حفظ</PrimaryBtn>
          </div>
        </form>
      </Modal>

      <Modal open={Boolean(confirmDel)} title="حذف الباقة" onClose={() => setConfirmDel(null)}>
        <p className="text-sm text-slate-600">سيتم حذف باقة «{confirmDel?.name}» من الكتالوج. لن يؤثر ذلك على اشتراكات اللاعبين الحالية. متابعة؟</p>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={() => setConfirmDel(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
          <button onClick={remove} className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white" data-testid="package-confirm-del">حذف</button>
        </div>
      </Modal>
    </div>
  );
}

// نافذة Bottom Sheet على الجوال (أسفل الشاشة) وModal عادية على الشاشات الأوسع — خاصة بتبويب المستخدمون والصلاحيات فقط
function SheetModal({ open, title, onClose, children }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center sm:p-4" onClick={onClose}>
      <div className="max-h-[88vh] w-full overflow-y-auto rounded-t-2xl bg-white p-5 shadow-2xl sm:max-w-md sm:rounded-xl sm:p-6" onClick={(e) => e.stopPropagation()}>
        <h3 className="mb-4 text-base font-bold text-slate-900">{title}</h3>
        {children}
      </div>
    </div>
  );
}

function UsersTab({ notify, currentUser }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [err, setErr] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await axios.get(`${API}/users`, auth()); setUsers(Array.isArray(res.data) ? res.data : []); } catch { setUsers([]); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const isSelf = (u) => currentUser && u && u.id === currentUser.id;

  const openForm = (u) => {
    setEditing(u || null);
    setForm(u ? { username: u.username, full_name: u.full_name || '', email: u.email || '', role: u.role, is_active: u.is_active !== false, can_communicate: !!u.can_communicate, financial_permissions: Array.isArray(u.financial_permissions) ? u.financial_permissions : [], password: '' }
      : { username: '', full_name: '', email: '', role: 'manager', is_active: true, can_communicate: false, financial_permissions: [], password: '' });
    setErr(null); setModal(true);
  };

  const togglePayrollPerm = (perm) => {
    const current = form.financial_permissions || [];
    setForm({ ...form, financial_permissions: current.includes(perm) ? current.filter((p) => p !== perm) : [...current, perm] });
  };

  const submit = async (e) => {
    e.preventDefault(); setErr(null);
    if (!form.username?.trim() || !form.full_name?.trim()) { setErr('اسم المستخدم والاسم الكامل مطلوبان'); return; }
    if (!editing && !form.password) { setErr('كلمة المرور مطلوبة للمستخدم الجديد'); return; }
    try {
      if (editing) {
        const payload = { full_name: form.full_name, email: form.email, role: form.role, is_active: form.is_active, can_communicate: form.can_communicate, financial_permissions: form.financial_permissions || [] };
        if (form.password) payload.password = form.password;
        if (form.username !== editing.username) payload.username = form.username;
        await axios.put(`${API}/users/${editing.id}`, payload, auth());
      } else {
        await axios.post(`${API}/users`, { username: form.username.trim(), password: form.password, full_name: form.full_name.trim(), email: form.email || '', role: form.role, is_active: form.is_active, can_communicate: form.can_communicate }, auth());
      }
      setModal(false); notify('تم الحفظ بنجاح'); load();
    } catch (e2) { setErr(errMsg(e2, 'فشل الحفظ')); }
  };

  const remove = async () => {
    try { await axios.delete(`${API}/users/${confirmDel.id}`, auth()); notify('تم حذف المستخدم'); setConfirmDel(null); load(); }
    catch (e2) { notify(errMsg(e2, 'تعذر الحذف')); setConfirmDel(null); }
  };

  const rowActions = (u) => [
    { icon: 'cog', label: 'تعديل', onClick: () => openForm(u), testId: 'user-edit-btn' },
    { icon: 'trash', label: 'حذف', onClick: () => setConfirmDel(u), testId: 'user-del-btn', danger: true },
  ];

  return (
    <div className="rounded-xl border border-slate-200 bg-white" data-testid="settings-users">
      <div className="flex flex-col gap-2 border-b border-slate-100 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <span className="text-sm font-bold text-slate-800">مستخدمو المنشأة ({users.length})</span>
        <PrimaryBtn onClick={() => openForm(null)} data-testid="user-add-btn" className="w-full justify-center sm:w-auto"><Icon name="plus" className="w-4 h-4" /> إضافة مستخدم</PrimaryBtn>
      </div>
      {loading ? <div className="space-y-2 p-4">{[1, 2, 3].map((i) => <div key={i} className="h-11 animate-pulse rounded-lg bg-slate-100" />)}</div>
        : users.length === 0 ? <div className="p-6"><EmptyState icon="users" text="لا يوجد مستخدمون" testId="users-empty" /></div>
        : (
          <>
            <div className="hidden overflow-x-auto sm:block">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-slate-100 text-right text-xs text-slate-500">
                  <th className="px-4 py-2 font-semibold">المستخدم</th>
                  <th className="px-2 py-2 font-semibold">اسم الدخول</th>
                  <th className="px-2 py-2 font-semibold">الدور</th>
                  <th className="px-2 py-2 font-semibold">الحالة</th>
                  <th className="px-2 py-2"></th>
                </tr></thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-b border-slate-50" data-testid="user-row">
                      <td className="px-4 py-2"><div className="font-semibold text-slate-800">{u.full_name || '—'}</div><div className="text-xs text-slate-400">{u.email || ''}</div></td>
                      <td className="px-2 py-2 text-slate-600" dir="ltr">
                        <div className="flex items-center gap-1.5">
                          <span>{u.username}</span>
                          <button type="button" onClick={() => { navigator.clipboard.writeText(u.username); notify('تم نسخ اسم المستخدم'); }} title="نسخ اسم المستخدم" className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-teal-700" data-testid="user-copy-username-btn"><Icon name="copy" className="w-3.5 h-3.5" /></button>
                        </div>
                      </td>
                      <td className="px-2 py-2"><span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{ROLE_LABELS[u.role] || u.role}</span></td>
                      <td className="px-2 py-2"><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${u.is_active !== false ? 'bg-teal-50 text-teal-700' : 'bg-red-50 text-red-600'}`}>{u.is_active !== false ? 'مفعل' : 'معطل'}</span></td>
                      <td className="px-2 py-2"><div className="flex justify-end"><ActionsMenu items={rowActions(u)} testId="user-actions-btn" /></div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="space-y-2 p-3 sm:hidden">
              {users.map((u) => (
                <div key={u.id} className="rounded-xl border border-slate-200 p-3" data-testid="user-card-mobile">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="truncate font-semibold text-slate-800">{u.full_name || '—'}</div>
                      <div className="truncate text-xs text-slate-400">{u.email || u.username}</div>
                      <div className="mt-0.5 text-xs text-slate-400" dir="ltr">{u.username}</div>
                    </div>
                    <ActionsMenu items={rowActions(u)} testId="user-actions-btn-mobile" />
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-600" data-testid="user-role-badge-mobile">{ROLE_LABELS[u.role] || u.role}</span>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${u.is_active !== false ? 'bg-teal-50 text-teal-700' : 'bg-red-50 text-red-600'}`} data-testid="user-status-badge-mobile">{u.is_active !== false ? 'مفعل' : 'معطل'}</span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

      <SheetModal open={modal} title={editing ? 'تعديل المستخدم' : 'إضافة مستخدم'} onClose={() => setModal(false)}>
        {err && <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="user-form-error">{err}</div>}
        <form onSubmit={submit} className="space-y-3">
          <Field label="الاسم الكامل" required><input className={inputCls} value={form.full_name || ''} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="user-input-fullname" /></Field>
          <Field label="اسم الدخول" required>
            <div className="flex items-center gap-2">
              <input dir="ltr" className={inputCls} value={form.username || ''} onChange={(e) => setForm({ ...form, username: e.target.value })} data-testid="user-input-username" />
              {editing && <button type="button" onClick={() => { navigator.clipboard.writeText(form.username || ''); notify('تم نسخ اسم المستخدم'); }} title="نسخ اسم المستخدم" className="shrink-0 rounded-lg border border-slate-200 p-2.5 text-slate-500 hover:bg-slate-50 hover:text-teal-700" data-testid="user-form-copy-username-btn"><Icon name="copy" className="w-4 h-4" /></button>}
            </div>
          </Field>
          <Field label="البريد الإلكتروني"><input dir="ltr" type="email" className={inputCls} value={form.email || ''} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
          <Field label={editing ? 'كلمة مرور جديدة (اتركها فارغة للإبقاء)' : 'كلمة المرور'} required={!editing}>
            <input dir="ltr" type="password" className={inputCls} value={form.password || ''} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-input-password" />
          </Field>
          <Field label="الدور">
            {editing && isSelf(editing) ? (
              <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-500" data-testid="user-role-self-locked">
                <span>{ROLE_LABELS[form.role] || form.role}</span>
                <span className="text-xs">لا يمكنك تغيير دورك الخاص</span>
              </div>
            ) : (
              <select className={inputCls} value={form.role || 'manager'} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="user-input-role">
                {Object.entries(ASSIGNABLE_ROLE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            )}
          </Field>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={form.is_active !== false} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} /> حساب مفعل
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700" data-testid="user-can-communicate-row">
            <input type="checkbox" checked={!!form.can_communicate} onChange={(e) => setForm({ ...form, can_communicate: e.target.checked })} data-testid="user-input-can-communicate" /> صلاحية التواصل عبر واتساب (رسائل جاهزة)
          </label>
          {editing && (
            <div className="rounded-lg border border-slate-200 p-3" data-testid="user-payroll-permissions-box">
              <div className="mb-2 text-xs font-bold text-slate-600">صلاحيات الرواتب (ملاحظة: مدير المنشأة/الإداري/أمين الصندوق يملكون هذه الصلاحيات تلقائياً)</div>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={(form.financial_permissions || []).includes('view_payroll')} onChange={() => togglePayrollPerm('view_payroll')} data-testid="user-input-view-payroll" /> عرض الرواتب والسلف
              </label>
              <label className="mt-1.5 flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={(form.financial_permissions || []).includes('record_payroll_payment')} onChange={() => togglePayrollPerm('record_payroll_payment')} data-testid="user-input-record-payroll" /> تسجيل دفعات الرواتب والسلف
              </label>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setModal(false)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
            <PrimaryBtn type="submit" data-testid="user-form-submit">حفظ</PrimaryBtn>
          </div>
        </form>
      </SheetModal>

      <SheetModal open={Boolean(confirmDel)} title="حذف المستخدم" onClose={() => setConfirmDel(null)}>
        <p className="text-sm text-slate-600">سيتم حذف حساب «{confirmDel?.full_name || confirmDel?.username}» نهائياً. متابعة؟</p>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={() => setConfirmDel(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
          <button onClick={remove} className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white" data-testid="user-confirm-del">حذف</button>
        </div>
      </SheetModal>
    </div>
  );
}
